import React, { useState, useRef, useEffect } from 'react';
import { Check, X } from '@phosphor-icons/react';
import DatePickerDMY from './DatePickerDMY';

export function InlineEdit({ value, onSave, type = 'text', prefix = '', suffix = '', className = '', mono = false, placeholder = '', displayValue: customDisplay }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = () => {
    const finalVal = type === 'number' ? parseFloat(editValue) || 0 : editValue;
    if (finalVal !== value) {
      onSave(finalVal);
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value ?? '');
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  if (editing) {
    if (type === 'date') {
      return (
        <div className="flex items-center gap-1" data-testid="inline-edit-active">
          <DatePickerDMY
            value={editValue}
            onChange={(v) => { setEditValue(v); }}
            className="text-sm w-full"
          />
          <button onClick={handleSave} className="p-0.5 text-emerald-600 hover:text-emerald-800 flex-shrink-0" data-testid="inline-edit-save">
            <Check size={14} weight="bold" />
          </button>
          <button onClick={handleCancel} className="p-0.5 text-zinc-400 hover:text-zinc-700 flex-shrink-0" data-testid="inline-edit-cancel">
            <X size={14} weight="bold" />
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1" data-testid="inline-edit-active">
        <input
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className={`border border-blue-400 bg-blue-50 px-2 py-0.5 text-sm outline-none focus:ring-1 focus:ring-blue-400 w-full ${mono ? 'font-mono' : ''}`}
          step={type === 'number' ? '0.01' : undefined}
          data-testid="inline-edit-input"
        />
        <button onClick={handleSave} className="p-0.5 text-emerald-600 hover:text-emerald-800 flex-shrink-0" data-testid="inline-edit-save">
          <Check size={14} weight="bold" />
        </button>
        <button onClick={handleCancel} className="p-0.5 text-zinc-400 hover:text-zinc-700 flex-shrink-0" data-testid="inline-edit-cancel">
          <X size={14} weight="bold" />
        </button>
      </div>
    );
  }

  const displayValue = customDisplay || (value !== null && value !== undefined && value !== ''
    ? `${prefix}${type === 'number' ? parseFloat(value).toFixed(2) : value}${suffix}`
    : placeholder || '—');

  return (
    <span
      onClick={() => { setEditValue(value ?? ''); setEditing(true); }}
      className={`cursor-pointer hover:bg-blue-50 hover:text-blue-700 px-1 py-0.5 -mx-1 transition-colors border border-transparent hover:border-blue-200 inline-block ${mono ? 'font-mono' : ''} ${className}`}
      title="Click to edit"
      data-testid="inline-edit-display"
    >
      {displayValue}
    </span>
  );
}
